import { Typography, Badge, Tag, Collapse } from 'antd';
import { StyledTaskTemplates } from './TaskTemplates.styled';
import useTaskTemplates from './useTaskTemplates.hook';
import ActivityTypeIcon from '@/shared/ActivityTypeIcon/ActivityTypeIcon';
import ActivityTypes from '@/constants/ActivityTypes';
import PreviewTask from '@/shared/PreviewTask/PreviewTask';
import AssignTaskDrawer from './AssignTaskDrawer/AssignTaskDrawer';
import { CheckCircleFilled } from '@ant-design/icons';
import Button from '@/shared/Button/Button';
import Colors from '@/constants/Colors';
import Icon from '@ant-design/icons/lib/components/Icon';
import { Timer } from '@styled-icons/boxicons-regular';
import { useContext } from 'react';
import { NavigationContext } from '@/shared/NavigationContext/NavigationContext';
import { tags } from '@/constants/Tags';
import { capitalize } from '@/modules/stringUtils';

const { Paragraph } = Typography;
const { Panel } = Collapse;

export default function TaskTemplates({ theme, themeInClass, onFinishAction }) {
  const { state, actions } = useTaskTemplates({ theme });

  return (
    <StyledTaskTemplates>
      {state.showGrouped ? (
        <GroupedTemplates
          state={state}
          actions={actions}
          themeInClass={themeInClass}
        />
      ) : (
        <SimpleLayoutTemplates
          state={state}
          actions={actions}
          themeInClass={themeInClass}
        />
      )}
      {state.selectedAction &&
        {
          preview: (
            <PreviewTask
              task={state.templatesData[state.selectedTemplate?.id]}
              onClose={() => actions.setSelectedAction(null, null)}
            />
          ),
          assign: (
            <AssignTaskDrawer
              taskTemplate={state.selectedTemplate}
              onClose={() => actions.setSelectedAction(null, null)}
              onFinish={() => {
                actions.setSelectedAction(null, null);
                onFinishAction();
              }}
            />
          ),
        }[state.selectedAction]}
    </StyledTaskTemplates>
  );
}

function GroupedTemplates({ state, actions, themeInClass }) {
  const navigationContext = useContext(NavigationContext);
  const tagId = navigationContext.state.selectedTagId;
  const templateTags = tags.filter((tag) => !tagId || tag.id == tagId);

  const templateTagsWithContent = templateTags.reduce((acc, tag) => {
    const taskTemplates = state.groupedTemplates[tag.id] || [];
    return acc + (taskTemplates.length > 0 ? 1 : 0);
  }, 0);

  return (
    templateTagsWithContent > 0 && (
      <>
        <h3 className="mt">Assign tasks to your students</h3>
        <div className="templates-container">
          {templateTags.map((tag) => {
            const taskTemplates = state.groupedTemplates[tag.id] || [];
            return taskTemplates.length ? (
              <Collapse
                defaultActiveKey={
                  templateTags.length == 1 || templateTagsWithContent == 1
                    ? ['1']
                    : []
                }
                className="template-level"
                key={`${tag.id}-${templateTags.length == 1}`}
              >
                <Panel
                  header={
                    <>
                      <span className="title">{tag.englishName} tasks</span>
                      <div className="card-grades">
                        <span>Grades</span>
                        <span className="grades-list">
                          {tag.suggestedGrades.map((grade) => (
                            <Tag key={grade}>{grade}</Tag>
                          ))}
                        </span>
                      </div>
                    </>
                  }
                  key="1"
                >
                  <div className="templates">
                    {taskTemplates.map((template) => {
                      const assignedToClass =
                        themeInClass?.taskTemplatesEnabled.some(
                          (taskTemplateEnabled) =>
                            taskTemplateEnabled == template.id
                        );
                      return (
                        <TaskTemplate
                          name={template.name}
                          key={template.id}
                          template={template}
                          assignedToClass={assignedToClass}
                          state={state}
                          actions={actions}
                        />
                      );
                    })}
                  </div>
                </Panel>
              </Collapse>
            ) : null;
          })}
        </div>
      </>
    )
  );
}

function SimpleLayoutTemplates({ state, actions, themeInClass }) {
  return (
    state.templates.length > 0 && (
      <>
        <h3 className="mt">Assign tasks to your students</h3>
        <div className="templates-container">
          <div className="templates">
            {state.templates.map((template) => {
              const assignedToClass = themeInClass?.taskTemplatesEnabled.some(
                (taskTemplateEnabled) => taskTemplateEnabled == template.id
              );
              return (
                <TaskTemplate
                  simpleLayout
                  name={capitalize(template.level)}
                  key={template.id}
                  template={template}
                  assignedToClass={assignedToClass}
                  state={state}
                  actions={actions}
                />
              );
            })}
          </div>
        </div>
      </>
    )
  );
}

function TaskTemplate({
  template,
  name,
  assignedToClass,
  state,
  actions,
  simpleLayout,
}) {
  return (
    <div
      className={`template ${simpleLayout ? template.level : ''}`}
      key={template.id}
    >
      <div className="template-header">
        <div className="template-name">{name}</div>
        {assignedToClass ? (
          <Tag color="green" icon={<CheckCircleFilled />}>
            assigned
          </Tag>
        ) : null}

        <div className="actions">
          <Button
            type="primary"
            ghost
            className="preview-btn"
            onClick={() => actions.setSelectedAction('preview', template)}
            loading={state.loading && state.selectedTemplate?.id == template.id}
          >
            Preview
          </Button>
          <Button
            type="primary"
            className="assign-btn"
            onClick={() => actions.setSelectedAction('assign', template)}
          >
            Assign{assignedToClass ? ' again' : ''}
          </Button>
        </div>
      </div>
      <div className="template-content">
        <div className="activity-types">
          {countActivityTypes(template.taskTemplateActivities).map(
            (activitiesCount, index) => (
              <div key={activitiesCount.type} className="activity-type">
                {activitiesCount.count > 1 ? (
                  <Badge
                    count={activitiesCount.count}
                    size="small"
                    color={Colors.text}
                  >
                    <ActivityTypeIcon type={activitiesCount.type} size={20} />
                  </Badge>
                ) : (
                  <ActivityTypeIcon type={activitiesCount.type} size={20} />
                )}
                <span className="activity-type-name">
                  {
                    ActivityTypes.withLabels.find(
                      (activityType) =>
                        activityType.value == activitiesCount.type
                    )?.[activitiesCount.count > 1 ? 'label' : 'labelSingular']
                  }
                </span>
              </div>
            )
          )}
        </div>

        <div className="description">
          {template.duration ? (
            <Paragraph className="duration">
              <Icon className="duration-icon" component={Timer} />
              Estimated duration: {template.duration} min.
            </Paragraph>
          ) : null}
          <Paragraph
            ellipsis={{
              expandable: true,
              rows: 8,
              symbol: 'expand',
            }}
            className="description-text"
          >
            {template.description.map((line, index) => (
              <p key={index} dangerouslySetInnerHTML={{ __html: line }}></p>
            ))}
          </Paragraph>
        </div>
      </div>
    </div>
  );
}

function countActivityTypes(taskTemplateActivities) {
  const countAsObject = taskTemplateActivities.reduce(
    (countObject, taskTemplateActivity) => {
      const { type } = taskTemplateActivity.activity;
      if (!countObject[type]) {
        countObject[type] = 0;
      }
      countObject[type]++;
      return countObject;
    },
    {}
  );
  return Object.keys(countAsObject).map((type) => ({
    type,
    count: countAsObject[type],
  }));
}
